import { useEffect, useRef, useState } from "react";
import { Gauge, Landmark, Zap } from "lucide-react";
import type { NicheConfig } from "../../types/niche";
import type { PreviewResponse } from "../../types/preview";
import type { ReleasedDocument } from "../../types/api";
import type { SaldoFormData } from "../../lib/saldoSchema";
import { ApiError, generateSaldoPreview } from "../../lib/api";
import { buildLocalSaldoPreview } from "../../lib/saldoMocks";
import { Stepper } from "./Stepper";
import { SaldoForm } from "./SaldoForm";
import type { FormReturnState } from "./ContractForm";
import { ProcessingScreen } from "./ProcessingScreen";
import { DocumentPreview } from "./DocumentPreview";
import type { Seal } from "./ConversionBlock";

type FlowPhase = "form" | "processing" | "preview";

const STEPPER_INDEX: Record<FlowPhase, number> = { form: 0, processing: 1, preview: 1 };

const STAGES = [
  "1. Auditando enquadramento na Lei nº 12.865/13...",
  "2. Verificando prazos de liquidação BCB nº 150/21...",
  "3. Gerando Notificação Extrajudicial com cominação de mora...",
];

const CONVERSION = {
  title: "Liberar Documento Completo em PDF + Roteiro de Envio",
  seals: [
    { id: "emissao", label: "Emissão imediata", icon: Zap },
    { id: "fundamentacao", label: "Fundamentação na Lei nº 12.865/13", icon: Landmark },
    { id: "prazo", label: "Limite cautelar regulamentar de até 72 horas (Resolução BCB nº 150/2021)", icon: Gauge },
  ] as Seal[],
};

const saldoFacts = (data: SaldoFormData) => [
  { k: "Instituição", v: data.instituicao === "Outro" ? data.instituicaoOutra.trim() : data.instituicao },
  { k: "Titular", v: data.tipoTitular === "PF" ? "Pessoa física" : "Pessoa jurídica" },
  { k: "Motivo alegado", v: data.motivoAlegado.trim().slice(0, 120) },
];

function formReturnFor(err: ApiError): FormReturnState | null {
  switch (err.code) {
    case "DEADLINE_FUTURE":
      return { step: 1, expiredDays: null, notice: err.message };
    case "RATE_LIMITED":
    case "VALIDATION_ERROR":
      return { step: 2, expiredDays: null, notice: err.message };
    default:
      return null;
  }
}

export function SaldoRetidoFlow({ config }: { config: NicheConfig }) {
  const [phase, setPhase] = useState<FlowPhase>("form");
  const [data, setData] = useState<SaldoFormData | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [formReturn, setFormReturn] = useState<FormReturnState | null>(null);
  const [released, setReleased] = useState<ReleasedDocument | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const prevPhase = useRef<FlowPhase>(phase);
  const requestId = useRef(0);

  useEffect(() => {
    const changed = prevPhase.current !== phase;
    prevPhase.current = phase;
    if (!changed || phase === "preview") return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    sectionRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [phase]);

  const onSubmit = (values: SaldoFormData) => {
    const id = ++requestId.current;
    setData(values);
    setPreview(null);
    setReleased(null);
    setFormReturn(null);
    setPhase("processing");
    generateSaldoPreview(values)
      .then((res) => {
        if (id === requestId.current) setPreview(res);
      })
      .catch((err: unknown) => {
        if (id !== requestId.current) return;
        const back = err instanceof ApiError ? formReturnFor(err) : null;
        if (back) {
          requestId.current += 1;
          setFormReturn(back);
          setPhase("form");
          return;
        }
        console.warn("Gepeto: falha ao gerar prévia no servidor; usando fallback local.", err instanceof ApiError ? err.code : err);
        setPreview(buildLocalSaldoPreview(values));
      });
  };

  const onEdit = () => {
    setFormReturn(null);
    setPhase("form");
  };

  return (
    <section ref={sectionRef} id="protocolo" data-testid="saldo-flow" data-phase={released ? "released" : phase} className="scroll-mt-20 border-b border-slate-800">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="rounded-md border border-slate-800 bg-slate-900">
          <div className="flex flex-col gap-6 border-b border-slate-800 p-6 sm:p-8">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-500">Protocolo de notificação extrajudicial</p>
            <Stepper current={released ? 2 : STEPPER_INDEX[phase]} />
          </div>
          {phase !== "preview" && (
            <div className="p-6 sm:p-8">
              {phase === "form" && <SaldoForm config={config} defaultValues={data ?? undefined} returnState={formReturn} onSubmit={onSubmit} />}
              {phase === "processing" && <ProcessingScreen stages={STAGES} ready={preview !== null} onDone={() => setPhase("preview")} />}
            </div>
          )}
        </div>
        {phase === "preview" && data && preview && (
          <div className="mt-10">
            <DocumentPreview
              facts={saldoFacts(data)}
              buyer={{ nomeCompleto: data.razaoSocialOuNome.trim(), documento: data.documentoTitular, email: data.email.trim() }}
              conversion={CONVERSION}
              preview={preview}
              released={released}
              price={config.price}
              priceNote={config.priceNote}
              onEdit={onEdit}
              onReleased={setReleased}
              onRegenerate={() => onSubmit(data)}
            />
          </div>
        )}
      </div>
    </section>
  );
}
